/**
 * Tarball service — builds gzipped tar archives for individual services
 * declared in `cloudgrid.yaml`.
 *
 * Used by the `cloudgrid plug` verb (Module 3) to produce one tarball part
 * per service for the multipart body that `POST /api/v2/entities/:id/plug`
 * expects.
 *
 * For each service `<svc>` declared in `cloudgrid.yaml.services`, the source
 * lives at `<projectRoot>/services/<svc>/`. The tarball is the gzip of THAT
 * directory's contents — equivalent to `tar -C <serviceDir> -czf - .`.
 *
 * In addition to a hardcoded set of safety-net excludes, this service honours
 * `.gitignore` and `.cloudgridignore` files at both the project root and the
 * service dir. We only handle the simple cases that map cleanly onto tar's
 * `--exclude` semantics — comments (`#`), blank lines, and negation lines
 * (`!foo`) are skipped.
 *
 * This module is pure logic — it never writes to stdout. The verb that calls
 * it owns all user-facing I/O.
 */

import { execFileSync } from 'node:child_process';
import {
  existsSync,
  mkdtempSync,
  readFileSync,
  rmSync,
  writeFileSync,
} from 'node:fs';
import { tmpdir } from 'node:os';
import { isAbsolute, join, normalize, relative, sep } from 'node:path';

import { CloudGridError } from '../utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface BuildServiceTarballOpts {
  /** Absolute path to the service source directory (e.g. /repo/services/web). */
  serviceDir: string;
  /** Absolute path to the project root (the dir holding cloudgrid.yaml). */
  projectRoot: string;
  /** Override DEFAULT_EXCLUDES if you genuinely need to. Optional. */
  defaultExcludes?: ReadonlyArray<string>;
  /**
   * Backlog 108 — when the service declares `build:` in yaml, the build
   * output directory (default `dist`, overridable via `build.output`) is
   * produced server-side by Cloud Build, so the local copy is wasted upload
   * bytes. Pass the directory name here to add it to the exclude set.
   *
   * Static services WITHOUT `build:` may legitimately ship a pre-built
   * `dist/` they're serving directly — those callers omit this option and
   * the tarball includes the directory. The pre-108 default unconditionally
   * excluded `dist/`, which silently dropped pre-built static assets and
   * left users staring at a blank image.
   */
  buildOutputDir?: string;
}

export interface BuildServiceTarballDeps {
  /**
   * Injectable `tar` runner — defaults to node's `execFileSync`. Exists so a
   * unit test can assert the spawn options (notably `COPYFILE_DISABLE=1`)
   * without shelling out to the system `tar`.
   */
  execFileSync?: typeof execFileSync;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hardcoded safety-net excludes, applied even when no .gitignore exists.
 *
 * Mirrors v0.7.7's deploy.ts plus a few common-sense additions (`.cloudgrid`,
 * `.DS_Store`).
 *
 * Per decision 018, `.env.example` IS shipped to the server (committed; the
 * server uses it for plug-time key-validation). We exclude only the
 * developer-machine dotenv files: `.env`, `.env.local`, `.env.*.local`.
 * The broader `.env.*` glob from earlier versions would have caught
 * `.env.example` and broken validation.
 *
 * Backlog 108 — `dist` is NO LONGER unconditionally excluded. The
 * audit on PR #338 caught the regression: pre-108 `type: static` services
 * may legitimately ship a pre-built `dist/` they serve directly. Static
 * services with `build:` declared opt into excluding their build output
 * via the `buildOutputDir` field on `BuildServiceTarballOpts`.
 */
export const DEFAULT_EXCLUDES: ReadonlyArray<string> = Object.freeze([
  'node_modules',
  '.git',
  '.cloudgrid',
  '.next',
  '__pycache__',
  '.venv',
  '.DS_Store',
  '*.log',
  '.env',
  '.env.local',
  '.env.*.local',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Source directory resolution (backlog 124)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Backlog 124 — resolve the on-disk directory the CLI should tarball for a
 * given service. Two paths:
 *
 * - When the service block declares `source.path`, that value (a relative
 *   directory under the project root) is the source dir.
 * - Otherwise the legacy `<projectRoot>/services/<svc>/` convention applies,
 *   so existing yaml continues to work without modification.
 *
 * The `source.path` value is re-validated against the same rules used by
 * `validateConfig` in `@cloudgrid/shared` so a hand-crafted yaml that skips
 * server-side validation still can't escape the project root or smuggle in
 * shell metacharacters.
 */
export function resolveServiceSourceDir(opts: {
  /** Service block from the parsed yaml. */
  serviceConfig: unknown;
  /** Service name (the key under `services:`). */
  serviceName: string;
  /** Absolute path to the project root (where `cloudgrid.yaml` lives). */
  projectRoot: string;
}): string {
  const cfg =
    typeof opts.serviceConfig === 'object' && opts.serviceConfig !== null
      ? (opts.serviceConfig as { source?: { path?: unknown } })
      : null;
  const rawPath = cfg?.source?.path;
  if (rawPath === undefined) {
    return join(opts.projectRoot, 'services', opts.serviceName);
  }
  if (typeof rawPath !== 'string' || rawPath.length === 0) {
    throw new CloudGridError(
      `services.${opts.serviceName}.source.path must be a non-empty string`,
    );
  }
  if (isAbsolute(rawPath)) {
    throw new CloudGridError(
      `services.${opts.serviceName}.source.path must be relative (got ${JSON.stringify(rawPath)})`,
    );
  }
  if (/\\/.test(rawPath) || /\0/.test(rawPath) || /\s/.test(rawPath)) {
    throw new CloudGridError(
      `services.${opts.serviceName}.source.path contains invalid characters: ${JSON.stringify(rawPath)}`,
    );
  }
  // Reject `..` traversal both literally (any `..` segment) and after
  // normalisation (a normalised path that escapes the project root). Two
  // checks because someone could write `foo/../bar` which normalises back
  // inside the root — still rejected as a defensive measure.
  if (rawPath.split('/').some((seg) => seg === '..')) {
    throw new CloudGridError(
      `services.${opts.serviceName}.source.path may not contain '..' segments: ${JSON.stringify(rawPath)}`,
    );
  }
  const normalised = normalize(join(opts.projectRoot, rawPath));
  const rel = relative(opts.projectRoot, normalised);
  if (rel.startsWith('..') || (rel.length > 0 && isAbsolute(rel))) {
    throw new CloudGridError(
      `services.${opts.serviceName}.source.path escapes the project root: ${JSON.stringify(rawPath)}`,
    );
  }
  // `relative` returns `''` when the path resolves to the project root.
  // `join(root, '')` collapses to `root` itself, which is what we want for
  // `source.path: '.'`. The `sep` reference keeps the import in use across
  // platforms where `normalize` may emit OS-specific separators.
  void sep;
  return normalised;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pattern collection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse an ignore-file's content into a list of patterns suitable for tar's
 * `--exclude` flag.
 *
 * Strips comments (lines starting with `#`), blank lines, and negation lines
 * (lines starting with `!`). Whitespace around each pattern is trimmed.
 */
function parseIgnoreFile(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('#')) continue;
    if (line.startsWith('!')) continue;
    out.push(line);
  }
  return out;
}

/**
 * Read an ignore file from disk if it exists, returning [] otherwise.
 */
function readIgnoreIfExists(path: string): string[] {
  if (!existsSync(path)) return [];
  try {
    const content = readFileSync(path, 'utf8');
    return parseIgnoreFile(content);
  } catch {
    // Unreadable file (permissions, etc.) — degrade gracefully rather than
    // failing the whole packaging step over a stray ignore file.
    return [];
  }
}

/**
 * Collect the deduplicated list of exclude patterns to feed `tar --exclude-from`.
 *
 * Order: defaults first, then projectRoot patterns (`.gitignore` then
 * `.cloudgridignore`), then serviceDir patterns (`.gitignore` then
 * `.cloudgridignore`). Duplicates are dropped (first occurrence wins).
 *
 * Exposed for testing; the verb itself only ever calls `buildServiceTarball`.
 */
export function collectExcludePatterns(opts: BuildServiceTarballOpts): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const push = (p: string) => {
    if (seen.has(p)) return;
    seen.add(p);
    ordered.push(p);
  };

  for (const def of opts.defaultExcludes ?? DEFAULT_EXCLUDES) {
    push(def);
  }

  // Backlog 108 — opt-in exclusion of the server-side build output dir.
  // Only added when the caller declares `build:` on this service (the
  // server produces this dir; shipping it from the client wastes bytes).
  if (opts.buildOutputDir) {
    push(opts.buildOutputDir);
  }

  for (const p of readIgnoreIfExists(join(opts.projectRoot, '.gitignore'))) {
    push(p);
  }
  for (const p of readIgnoreIfExists(
    join(opts.projectRoot, '.cloudgridignore')
  )) {
    push(p);
  }
  for (const p of readIgnoreIfExists(join(opts.serviceDir, '.gitignore'))) {
    push(p);
  }
  for (const p of readIgnoreIfExists(
    join(opts.serviceDir, '.cloudgridignore')
  )) {
    push(p);
  }

  return ordered;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tarball build
// ─────────────────────────────────────────────────────────────────────────────

/** 200 MB — generous safety margin above the server's expected upload limit. */
const TAR_MAX_BUFFER = 200 * 1024 * 1024;

/**
 * Build a gzipped tar of the service directory's contents.
 *
 * Equivalent to:
 *   `tar -czf - --exclude-from <tmp> -C <serviceDir> .`
 *
 * The exclude file is materialised in `os.tmpdir()` and removed in a `finally`
 * block. Any failure from `execFileSync` (non-zero exit, buffer overflow,
 * spawn error) is wrapped in a `CloudGridError` with the original message.
 */
export function buildServiceTarball(
  opts: BuildServiceTarballOpts,
  deps: BuildServiceTarballDeps = {},
): Buffer {
  const exec = deps.execFileSync ?? execFileSync;
  if (!existsSync(opts.serviceDir)) {
    throw new CloudGridError(
      `Service directory not found: ${opts.serviceDir}`
    );
  }

  const patterns = collectExcludePatterns(opts);

  // Namespace the prefix with the current pid so parallel test runs in other
  // suites that also use os.tmpdir() can't pollute this prefix and break the
  // "leaves no leftover dir" assertion. The pid is unique per process; the
  // mkdtempSync suffix keeps it unique within the process.
  const tmpDir = mkdtempSync(join(tmpdir(), `cg-tarball-excludes-${process.pid}-`));
  const excludeFile = join(tmpDir, 'excludes');
  try {
    writeFileSync(excludeFile, patterns.join('\n') + '\n', 'utf8');

    try {
      const result = exec(
        'tar',
        [
          '-czf',
          '-',
          '--exclude-from',
          excludeFile,
          '-C',
          opts.serviceDir,
          '.',
        ],
        {
          maxBuffer: TAR_MAX_BUFFER,
          stdio: ['ignore', 'pipe', 'pipe'],
          // macOS BSD `tar` ships `._*` AppleDouble sidecars carrying xattrs by
          // default. Webpack-based frameworks (Next.js, Vite) import them as
          // modules and fail the build with "stream did not contain valid
          // UTF-8". COPYFILE_DISABLE=1 suppresses the sidecars; a no-op on
          // GNU tar (Linux CI), so it's safe to always set. (Smoke Finding 7.)
          env: { ...process.env, COPYFILE_DISABLE: '1' },
        }
      );
      // No `encoding` set → tar's stdout comes back as a Buffer. The cast
      // pins the overload union (string | Buffer) the injectable type widens to.
      return result as Buffer;
    } catch (err) {
      const orig = err instanceof Error ? err.message : String(err);
      throw new CloudGridError(
        `Failed to package ${opts.serviceDir}: ${orig}`
      );
    }
  } finally {
    rmSync(tmpDir, { recursive: true, force: true });
  }
}
